import React from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  DocumentTextIcon,
  BriefcaseIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  CodeBracketIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  CogIcon,
  ServerIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ScaleIcon,
  KeyIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const TermsWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f3f4f6 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.03) 0%, transparent 70%);
    animation: ${gradientShift} 20s ease infinite;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  padding: var(--spacing-4xl) 0;
  position: relative;
  overflow: hidden;
  margin-top: -80px;
  padding-top: calc(var(--spacing-4xl) + 80px);
  
  @media (min-width: 768px) {
    margin-top: -90px;
    padding-top: calc(var(--spacing-4xl) + 90px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-xl);
  position: relative;
  z-index: 1;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  margin-bottom: var(--spacing-xl);
  animation: ${fadeIn} 0.6s ease-out;
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto var(--spacing-xl) auto;
  line-height: 1.8;
  animation: ${fadeIn} 1s ease-out;
`;

const LastUpdatedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.15);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 50px;
  color: white;
  font-size: 0.9rem;
  animation: ${fadeIn} 1.2s ease-out;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-3xl) var(--spacing-xl);
  position: relative;
  z-index: 1;
`;

const QuickNav = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-3xl);
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  animation: ${fadeIn} 0.6s ease-out;
`;

const QuickNavTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const QuickNavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
`;

const QuickNavItem = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  color: var(--text-secondary);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
    color: var(--davinci-blue);
    transform: translateX(4px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: var(--davinci-blue);
    flex-shrink: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: var(--spacing-2xl);
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-2xl);
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 58, 138, 0.1) 100%);
  border-radius: 12px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const SectionContent = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  color: var(--davinci-midnight);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const SectionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 600;
`;

const SectionText = styled.p`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-lg);
`;

const SubSection = styled.div`
  margin-bottom: var(--spacing-xl);
  padding-left: var(--spacing-lg);
  border-left: 2px solid rgba(59, 130, 246, 0.2);
`;

const SubSectionTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const SubSubSection = styled.div`
  margin-bottom: var(--spacing-lg);
  padding-left: var(--spacing-lg);
`;

const SubSubSectionTitle = styled.h4`
  color: var(--davinci-midnight);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-xl);
  position: relative;
  
  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:after {
    content: "✓";
    position: absolute;
    left: 5px;
    top: 8px;
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const UnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-lg) 0;
`;

const OrderedList = styled.ol`
  padding-left: var(--spacing-xl);
  margin: 0 0 var(--spacing-lg) 0;
  color: var(--text-secondary);
  
  li {
    line-height: 1.8;
    margin-bottom: var(--spacing-sm);
  }
`;

const ResponseTable = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.02) 0%, rgba(30, 58, 138, 0.02) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  overflow: hidden;
  margin: var(--spacing-xl) 0;
`;

const TableHeader = styled.div`
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  text-align: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(59, 130, 246, 0.03);
  }
`;

const PriorityBadge = styled.span<{ $priority: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$priority) {
      case 'Critical': return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      case 'High': return 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
      case 'Medium': return 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)';
      case 'Low': return 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
      default: return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    }
  }};
  color: white;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, var(--davinci-midnight) 0%, var(--davinci-blue) 100%);
  border-radius: 16px;
  padding: var(--spacing-2xl);
  margin-top: var(--spacing-3xl);
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.2);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.3) 100%);
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-xl);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  color: white;

  svg {
    width: 28px;
    height: 28px;
    color: white;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-xl);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  svg {
    width: 20px;
    height: 20px;
    opacity: 0.8;
  }
  
  a {
    color: white;
    text-decoration: none;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin: var(--spacing-lg) 0;
`;

const TermsOfUse: React.FC = () => {
  const sections = [
    { id: 'scope-services', title: 'Scope of Services', icon: <BriefcaseIcon /> },
    { id: 'service-levels', title: 'Service Level Objectives', icon: <ClockIcon /> },
    { id: 'payment-terms', title: 'Payment Terms', icon: <CpuChipIcon /> },
    { id: 'managed-it', title: 'Managed IT & Helpdesk', icon: <WrenchScrewdriverIcon /> },
    { id: 'infrastructure', title: 'IT Infrastructure', icon: <ServerIcon /> },
    { id: 'software-dev', title: 'Software Development', icon: <CodeBracketIcon /> },
    { id: 'incident-mgmt', title: 'Incident Management', icon: <ExclamationTriangleIcon /> },
    { id: 'out-of-scope', title: 'Out-of-Scope Services', icon: <CogIcon /> },
    { id: 'third-party', title: 'Third-Party Services', icon: <UserGroupIcon /> },
    { id: 'licensing', title: 'Licensing', icon: <KeyIcon /> },
    { id: 'security', title: 'Security & Compliance', icon: <ShieldCheckIcon /> },
    { id: 'changes', title: 'Policy Updates', icon: <PencilSquareIcon /> },
    { id: 'contact', title: 'Contact Support', icon: <ChatBubbleLeftRightIcon /> },
  ];

  return (
    <TermsWrapper>
      <HeroSection>
        <HeroContent>
          <IconWrapper>
            <BookOpenIcon />
          </IconWrapper>
          <HeroTitle>Terms of Use</HeroTitle>
          <HeroSubtitle>
            Service agreements, response times, and operational standards for our Managed IT, 
            Infrastructure, and Software Development services
          </HeroSubtitle>
          <LastUpdatedBadge>
            <InformationCircleIcon />
            Last Updated: August 29, 2025
          </LastUpdatedBadge>
        </HeroContent>
      </HeroSection>

      <Container>
        <ScrollAnimation>
          <QuickNav>
            <QuickNavTitle>
              <DocumentTextIcon />
              Service Framework
            </QuickNavTitle>
            <QuickNavGrid>
              {sections.map((section) => (
                <QuickNavItem key={section.id} href={`#${section.id}`}>
                  {section.icon}
                  {section.title}
                </QuickNavItem>
              ))}
            </QuickNavGrid>
          </QuickNav>
        </ScrollAnimation>

        <ContentGrid>
          <ScrollAnimation>
            <SectionCard id="scope-services">
              <SectionHeader>
                <SectionIcon>
                  <BriefcaseIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>1</SectionNumber>
                    Scope of Services
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>1.1 Service Coverage</SubSectionTitle>
                    <SectionText>
                      Davinci AI Solutions provides Managed IT Services, including system monitoring, 
                      helpdesk support, strategic IT planning, IT infrastructure projects, and software development.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>1.2 Applicable Agreements</SubSectionTitle>
                    <SectionText>
                      These Terms of Use are incorporated into our Master Services Agreement (MSA) with clients 
                      and govern day-to-day operations, usage standards, service delivery expectations, and response times.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="service-levels">
              <SectionHeader>
                <SectionIcon>
                  <ClockIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>2</SectionNumber>
                    General Service Level Objectives (SLOs)
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>2.1 Core and Non-Core Hours</SubSectionTitle>
                    <SubSubSection>
                      <SubSubSectionTitle>2.1.1 Core Business Hours</SubSubSectionTitle>
                      <HighlightBox>
                        Monday to Friday, 8:00 a.m. – 5:00 p.m. MST, excluding holidays
                      </HighlightBox>
                    </SubSubSection>
                    <SubSubSection>
                      <SubSubSectionTitle>2.1.2 Non-Core Hours</SubSubSectionTitle>
                      <SectionText>
                        All hours outside of Core Business Hours, including weekends and holidays. 
                        Support during these hours may incur additional fees.
                      </SectionText>
                    </SubSubSection>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>2.2 Incident Response Times</SubSectionTitle>
                    <ResponseTable>
                      <TableHeader>
                        <div>Priority Level</div>
                        <div>Core Business Hours</div>
                        <div>Non-Core Business Hours</div>
                      </TableHeader>
                      <TableRow>
                        <PriorityBadge $priority="Critical">Critical</PriorityBadge>
                        <div>30 minutes</div>
                        <div>60 minutes</div>
                      </TableRow>
                      <TableRow>
                        <PriorityBadge $priority="High">High</PriorityBadge>
                        <div>60 minutes</div>
                        <div>120 minutes</div>
                      </TableRow>
                      <TableRow>
                        <PriorityBadge $priority="Medium">Medium</PriorityBadge>
                        <div>120 minutes</div>
                        <div>Next business day</div>
                      </TableRow>
                      <TableRow>
                        <PriorityBadge $priority="Low">Low</PriorityBadge>
                        <div>120 minutes</div>
                        <div>Next business day</div>
                      </TableRow>
                    </ResponseTable>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>2.3 Resolution Times</SubSectionTitle>
                    <SectionText>
                      Response times are based on the priority of the incident as outlined above, while resolution 
                      times vary based on incident complexity, resource availability, and required remediation.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="payment-terms">
              <SectionHeader>
                <SectionIcon>
                  <CpuChipIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>3</SectionNumber>
                    Payment Terms
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>3.1 Invoicing</SubSectionTitle>
                    <SectionText>
                      Services are invoiced on the first day of each month with payment due within fifteen (15) days 
                      of the invoice date (Net 15 terms).
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>3.2 Accepted Payment Methods</SubSectionTitle>
                    <SectionText>
                      We accept the following payment methods:
                    </SectionText>
                    <UnorderedList>
                      <ListItem>E-Transfer (Interac e-Transfer)</ListItem>
                      <ListItem>Direct Deposit / Electronic Funds Transfer (EFT)</ListItem>
                      <ListItem>Cheque (preferably payable to "Davinci AI Solutions", though we also accept "HL Networks Inc." or "Davinci Technology Solutions")</ListItem>
                    </UnorderedList>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>3.3 Late Payments</SubSectionTitle>
                    <SectionText>
                      Late payments may result in service suspension and may incur interest charges at 1.5% per month 
                      (18% annually) on overdue amounts.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>3.4 Disputed Charges</SubSectionTitle>
                    <SectionText>
                      Any billing disputes must be reported within 15 days of invoice receipt. Disputed amounts under 
                      investigation will not accrue late fees during the review period.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="managed-it">
              <SectionHeader>
                <SectionIcon>
                  <WrenchScrewdriverIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>4</SectionNumber>
                    Managed IT Services and Helpdesk Support
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>4.1 Helpdesk Usage</SubSectionTitle>
                    <SectionText>
                      Clients may contact the helpdesk via phone, email, or portal. Upon receiving a ticket, 
                      Davinci will categorize it based on ITIL-aligned definitions, at our discretion, into one 
                      of the following categories:
                    </SectionText>
                    <OrderedList>
                      <li><strong>Incident:</strong> A disruption requiring immediate action.</li>
                      <li><strong>Problem:</strong> A recurrent or systemic issue that requires further investigation.</li>
                      <li><strong>Request:</strong> A user request for standard services, adjustments, or support tasks.</li>
                      <li><strong>Question:</strong> An inquiry requiring guidance or clarification.</li>
                      <li><strong>Information:</strong> A low-priority request or informational update requiring no immediate action.</li>
                    </OrderedList>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>4.2 Client Responsibilities</SubSectionTitle>
                    <SectionText>
                      Clients are responsible for providing necessary access, resources, and cooperation to enable 
                      efficient troubleshooting, remediation, or completion of helpdesk support tasks.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="infrastructure">
              <SectionHeader>
                <SectionIcon>
                  <ServerIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>5</SectionNumber>
                    IT Infrastructure Projects
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>5.1 Scope and Engagement</SubSectionTitle>
                    <SectionText>
                      IT Infrastructure Projects include upgrades, replacements, integrations, and other 
                      improvements to client IT environments.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>5.2 Change Orders</SubSectionTitle>
                    <SectionText>
                      Change Orders are required whenever new information or circumstances arise that introduce 
                      risk to the original project plan. If adjustments are necessary, Davinci will issue a 
                      Change Order that clearly outlines the modifications to the original scope, timeline, or costs. 
                      Change Orders will:
                    </SectionText>
                    <OrderedList>
                      <li>Reference the original project contract.</li>
                      <li>Require formal sign-off from the client before implementation.</li>
                      <li>Supersede the original contract terms, incorporating any updated terms agreed upon in light of the new variables.</li>
                    </OrderedList>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>5.3 Client Approval</SubSectionTitle>
                    <SectionText>
                      Clients are required to provide formal approval at critical stages, including:
                    </SectionText>
                    <OrderedList>
                      <li>Project initiation.</li>
                      <li>Acceptance of any Change Orders.</li>
                      <li>Final completion and acceptance of deliverables.</li>
                    </OrderedList>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="software-dev">
              <SectionHeader>
                <SectionIcon>
                  <CodeBracketIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>6</SectionNumber>
                    Software Development Projects
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>6.1 Scope and Usage</SubSectionTitle>
                    <SectionText>
                      Software Development Projects include custom AI solutions, workflow automation, and 
                      integration development, with each project governed by a project-specific Statement of Work (SOW).
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>6.2 Client Collaboration</SubSectionTitle>
                    <SectionText>
                      Active client collaboration is essential to project success. Clients agree to provide 
                      timely feedback, participate in testing phases, and review milestones as scheduled.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>6.3 Intellectual Property</SubSectionTitle>
                    <SubSubSection>
                      <SubSubSectionTitle>6.3.1 Deliverable Ownership</SubSubSectionTitle>
                      <SectionText>
                        The intellectual property (IP) rights of any code or software solution developed 
                        specifically for the client will transfer to the client upon project completion.
                      </SectionText>
                    </SubSubSection>
                    <SubSubSection>
                      <SubSubSectionTitle>6.3.2 Retained Knowledge</SubSubSectionTitle>
                      <SectionText>
                        While the client holds the IP of the final deliverables, Davinci retains the knowledge, 
                        skills, and processes gained during project development. Davinci reserves the right to 
                        apply this expertise in future projects or develop similar solutions using the general 
                        learnings acquired, without reusing specific client assets or proprietary configurations.
                      </SectionText>
                    </SubSubSection>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>6.4 Maintenance and Updates</SubSectionTitle>
                    <SectionText>
                      Post-project support may be provided under a separate agreement, or through our 
                      Managed IT Services if applicable.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="incident-mgmt">
              <SectionHeader>
                <SectionIcon>
                  <ExclamationTriangleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>7</SectionNumber>
                    Incident Management and Escalation
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>7.1 Ticket Categorization</SubSectionTitle>
                    <SectionText>
                      All helpdesk requests are classified as incidents, problems, requests, questions, or 
                      information, with response priorities based on category and impact.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>7.2 Escalation Protocols</SubSectionTitle>
                    <SectionText>
                      For incidents requiring specialized expertise or additional resources, tickets may be 
                      escalated to relevant teams. Clients will be informed of escalations and the expected 
                      timeline for resolution.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="out-of-scope">
              <SectionHeader>
                <SectionIcon>
                  <CogIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>8</SectionNumber>
                    Out-of-Scope Services
                  </SectionTitle>
                  <SectionText>
                    Major infrastructure overhauls, new implementations not covered in project SOWs, and 
                    third-party software management are out of scope unless specified in an SOW. 
                    Out-of-scope requests may be managed as separate projects under unique terms.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="third-party">
              <SectionHeader>
                <SectionIcon>
                  <UserGroupIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>9</SectionNumber>
                    Third-Party Services and Integrations
                  </SectionTitle>
                  <SectionText>
                    Davinci may use third-party providers to support our services (e.g., cloud storage, software). 
                    While we liaise with vendors for continuity, Davinci is not liable for third-party outages.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="licensing">
              <SectionHeader>
                <SectionIcon>
                  <KeyIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>10</SectionNumber>
                    Licensing and Software Usage
                  </SectionTitle>
                  <SectionText>
                    Any software provided by Davinci is licensed, not sold, for use exclusively in connection 
                    with our services. Unauthorized modifications, reverse engineering, or redistribution is prohibited.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="security">
              <SectionHeader>
                <SectionIcon>
                  <ShieldCheckIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>11</SectionNumber>
                    Data Security and Compliance
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>11.1 Client Security Responsibilities</SubSectionTitle>
                    <SectionText>
                      Clients must follow recommended security protocols, including secure password management 
                      and access control, to help prevent unauthorized access.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>11.2 Compliance</SubSectionTitle>
                    <SectionText>
                      Davinci adheres to Canadian and U.S. data protection standards, and all personal 
                      information is handled according to PIPEDA and relevant laws.
                    </SectionText>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="changes">
              <SectionHeader>
                <SectionIcon>
                  <PencilSquareIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>12</SectionNumber>
                    Changes to the Terms of Use
                  </SectionTitle>
                  <SectionText>
                    Davinci reserves the right to update these Terms of Use as required. Clients will 
                    receive a minimum 30 days' notice of any significant updates.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>
        </ContentGrid>

        <ScrollAnimation>
          <ContactCard id="contact">
            <ContactContent>
              <ContactTitle>
                <ChatBubbleLeftRightIcon />
                Contact and Support
              </ContactTitle>
              <SectionText style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: 'var(--spacing-xl)' }}>
                <strong>13.1 Helpdesk Contact:</strong>
              </SectionText>
              <ContactGrid>
                <ContactItem>
                  <PhoneIcon />
                  <a href="tel:+14038799343">403-879-9343</a>
                </ContactItem>
                <ContactItem>
                  <EnvelopeIcon />
                  <a href="mailto:info@davincisolutions.ai">info@davincisolutions.ai</a>
                </ContactItem>
                <ContactItem>
                  <GlobeAltIcon />
                  <a href="https://support.davincisolutions.ai" target="_blank" rel="noopener noreferrer">
                    support.davincisolutions.ai
                  </a>
                </ContactItem>
              </ContactGrid>
            </ContactContent>
          </ContactCard>
        </ScrollAnimation>
      </Container>
    </TermsWrapper>
  );
};

export default TermsOfUse;